import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, ArrowLeft, Loader2, LogOut, RefreshCw, Stethoscope } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const ZONE_STYLE = {
  resilience: "text-emerald-500",
  neutral: "text-amber-500",
  fire: "text-red-500",
};

const Card = ({ label, value, sub, testId }) => (
  <div data-testid={testId} className="rounded-lg border border-border bg-card p-4">
    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-1 font-heading text-2xl font-bold">{value ?? "—"}</div>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export default function PlatformHmde() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [liveCheck, setLiveCheck] = useState(null);

  const load = async () => {
    try {
      const [h, a] = await Promise.all([
        api.get("/platform/hmde/health"),
        api.get("/platform/hmde/activity?limit=100"),
      ]);
      setHealth(h.data);
      setActivity(a.data);
    } catch {
      toast.error("Could not load HMDE health data.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runLiveCheck = async () => {
    setChecking(true);
    try {
      const r = await api.get("/platform/diagnostics/hmde");
      setLiveCheck(r.data);
      r.data.ok ? toast.success("Live Claude check passed.") : toast.error("Live Claude check FAILED.");
    } catch {
      toast.error("Diagnostics call failed.");
    } finally {
      setChecking(false);
    }
  };

  const t = health?.today, w = health?.week;
  const zoneTotal = w ? Math.max(1, w.zones.resilience + w.zones.neutral + w.zones.fire) : 1;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-teal))" }}>
              <Activity className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight">Haruki Health</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>
                Admin · HMDE Health
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/platform" className="text-xs text-muted-foreground hover:text-foreground" data-testid="hmde-back-platform">
              <ArrowLeft className="mr-1 inline h-3 w-3" />Platform overview
            </Link>
            <span className="hidden text-xs text-muted-foreground sm:inline">{user?.email}</span>
            <Button data-testid="hmde-logout-button" variant="ghost" size="sm"
                    onClick={async () => { await logout(); nav("/"); }}>
              <LogOut className="mr-2 h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold">HMDE Health</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Scoring engine status — model <span className="font-mono">{health?.model || "…"}</span>
              {health && (health.key_present
                ? <span className="ml-2 text-emerald-500">key configured</span>
                : <span className="ml-2 text-red-500">KEY MISSING</span>)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="hmde-refresh-button" variant="outline" size="sm" onClick={load}>
              <RefreshCw className="mr-1.5 h-3 w-3" /> Refresh
            </Button>
            <Button data-testid="hmde-live-check-button" size="sm" onClick={runLiveCheck} disabled={checking}>
              {checking ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Stethoscope className="mr-1.5 h-3 w-3" />}
              Run live Claude check
            </Button>
          </div>
        </div>

        {liveCheck && (
          <div data-testid="hmde-live-check-result"
               className={`mt-4 rounded-md border px-4 py-2.5 text-sm ${liveCheck.ok ? "border-emerald-500/40 text-emerald-500" : "border-red-500/40 text-red-500"}`}>
            {liveCheck.ok ? "Live check OK — Claude responded correctly." : `Live check FAILED: ${liveCheck.error}`}
          </div>
        )}

        {loading ? (
          <div className="mt-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Card testId="hmde-card-scored-today" label="Scored today" value={t?.scored}
                    sub={`${w?.scored ?? 0} in last 7 days`} />
              <Card testId="hmde-card-latency" label="Avg response time"
                    value={t?.avg_latency_ms != null ? `${(t.avg_latency_ms / 1000).toFixed(1)}s` : (w?.avg_latency_ms != null ? `${(w.avg_latency_ms / 1000).toFixed(1)}s` : "—")}
                    sub="scoring call latency" />
              <Card testId="hmde-card-success" label="Claude success rate"
                    value={t?.success_rate != null ? `${Math.round(t.success_rate * 100)}%` : (w?.success_rate != null ? `${Math.round(w.success_rate * 100)}%` : "—")}
                    sub={`week: ${w?.success_rate != null ? Math.round(w.success_rate * 100) + "%" : "—"}`} />
              <Card testId="hmde-card-fallback" label="Fallbacks" value={t?.fallback ?? 0}
                    sub={`${w?.fallback ?? 0} in last 7 days`} />
              <Card testId="hmde-card-avg-score" label="Avg score" value={t?.avg_score ?? w?.avg_score}
                    sub="real scores only" />
            </div>

            <div className="mt-6 rounded-lg border border-border bg-card p-4" data-testid="hmde-zone-distribution">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Zone distribution — last 7 days</div>
              <div className="mt-3 flex h-3 w-full overflow-hidden rounded-full bg-muted">
                <div className="bg-emerald-500" style={{ width: `${(w.zones.resilience / zoneTotal) * 100}%` }} />
                <div className="bg-amber-500" style={{ width: `${(w.zones.neutral / zoneTotal) * 100}%` }} />
                <div className="bg-red-500" style={{ width: `${(w.zones.fire / zoneTotal) * 100}%` }} />
              </div>
              <div className="mt-2 flex gap-5 text-xs text-muted-foreground">
                <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500" />Resilience {w.zones.resilience}</span>
                <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-500" />Neutral {w.zones.neutral}</span>
                <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500" />Fire {w.zones.fire}</span>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-border bg-card p-4" data-testid="hmde-last-error">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Last API error</div>
              {health.last_error ? (
                <div className="mt-2 text-sm">
                  <span className="text-red-500">{health.last_error.error}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({health.last_error.source} · {new Date(health.last_error.at).toLocaleString()})
                  </span>
                </div>
              ) : (
                <div className="mt-2 text-sm text-muted-foreground">No API errors recorded.</div>
              )}
            </div>

            <h2 className="mt-10 font-heading text-lg font-bold">Recent HMDE activity</h2>
            <div className="mt-3 overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm" data-testid="hmde-activity-table">
                <thead className="border-b border-border bg-muted/30 text-[11px] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5">User</th>
                    <th className="px-4 py-2.5">Agency</th>
                    <th className="px-4 py-2.5">Date</th>
                    <th className="px-4 py-2.5">Score</th>
                    <th className="px-4 py-2.5">Zone</th>
                    <th className="px-4 py-2.5">Fallback</th>
                    <th className="px-4 py-2.5">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No completed scenarios yet.</td></tr>
                  )}
                  {activity.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0">
                      <td className="px-4 py-2">
                        <div>{row.user_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{row.user_email}</div>
                      </td>
                      <td className="px-4 py-2">{row.agency_name || "—"}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {row.date ? new Date(row.date).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-2 font-mono">{row.fallback ? "—" : row.score}</td>
                      <td className={`px-4 py-2 capitalize ${ZONE_STYLE[row.zone] || ""}`}>
                        {row.fallback ? "not scored" : row.zone}
                      </td>
                      <td className="px-4 py-2">{row.fallback ? <span className="text-red-500">yes</span> : "no"}</td>
                      <td className="px-4 py-2 text-xs">{(row.incident_category || "").replace(/_/g, " ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
